import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

// Images
import coverImage from "@/assets/blog/pourquoi-clients-partent/couverture.webp";
import raisonsImage from "@/assets/blog/pourquoi-clients-partent/raisons-depart-clients-camembert.webp";
import frequenceImage from "@/assets/blog/pourquoi-clients-partent/frequence-visite-client-chute-risque.webp";
import reengagementImage from "@/assets/blog/pourquoi-clients-partent/reengagement-client-zones-recuperation.webp";

const PourquoiClientsPartent = () => {
  const jsonLdSchemas = [
    generateWebPageSchema(
      "Pourquoi 68 % des clients partent sans jamais se plaindre (et comment les retenir)",
      seoConfig.blogPourquoiClientsPartent.description,
      "/pourquoi-clients-partent"
    ),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "Pourquoi les clients partent", path: "/pourquoi-clients-partent" }
    ]),
    generateArticleSchema(
      "Pourquoi 68 % des clients partent sans jamais se plaindre (et comment les retenir)",
      seoConfig.blogPourquoiClientsPartent.description,
      "https://unotif.com/pourquoi-clients-partent",
      "2026-04-06"
    )
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogPourquoiClientsPartent} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className="text-[#0c97a9] hover:underline">Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">Pourquoi les clients partent</span>
            </nav>

            {/* H1 */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              Pourquoi 68 % des clients partent sans jamais se plaindre (et comment les retenir)
            </h1>

            {/* Cover Image */}
            <img
              src={coverImage}
              alt="Illustration des clients qui partent sans se plaindre avec analyse du sentiment de négligence et fidélisation client"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Introduction */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Vos clients ont l'air satisfaits. Ils sourient, ils paient, ils disent "à bientôt". Et puis ils ne reviennent plus. Sans explication, sans réclamation, sans signe apparent. Ce scénario n'est pas une exception : il est la règle.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              68 % des clients qui quittent une entreprise le font parce qu'ils <strong className="font-semibold">se sentaient négligés</strong>, pas parce qu'ils avaient un problème (Harvard Business Review). Ils n'ont pas pris la peine de se plaindre parce que, dans leur tête, c'était déjà terminé. Cet article explique pourquoi ce phénomène est si fréquent, comment détecter les signaux avant-coureurs d'un <strong className="font-semibold">client sur le départ</strong>, et quelles actions concrètes permettent de le retenir avant qu'il franchisse la porte pour la dernière fois. Pour une stratégie de <strong className="font-semibold">fidélisation client</strong> complète, notre guide sur <Link to="/fidelisation-client-guide" className="text-[#0c97a9] hover:underline">comment fidéliser ses clients</Link> couvre le sujet de A à Z.
            </p>

            {/* Section 1 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Le départ silencieux : un phénomène plus fréquent qu'on ne le croit
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La plupart des commerçants et prestataires partent d'une conviction : si un client ne se plaint pas, c'est qu'il est content. Cette conviction est fausse, et elle coûte cher.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les données de la Harvard Business Review le montrent avec précision : 68 % des départs s'expliquent par un <strong className="font-semibold">sentiment de négligence</strong>, pas par une insatisfaction liée au produit. Seulement 14 % des clients partent à cause d'un problème avec l'offre elle-même. 9 % partent pour une raison concurrentielle, 9 % pour un déménagement ou changement de situation personnelle.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              Le silence d'un client n'est pas un signal positif. C'est un signal d'indifférence. Si votre client ne se plaint pas, ce n'est pas parce qu'il n'a pas de raison de partir : c'est parce qu'il ne croit pas que ça changerait quelque chose. Le problème de se plaindre, dans son esprit, est plus grand que le problème lui-même.
            </p>

            {/* Image: raisons-depart-clients-camembert */}
            <img
              src={raisonsImage}
              alt="Graphique en camembert des raisons du départ client avec sentiment de négligence majoritaire"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Section 2 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Les 4 vraies raisons qui font partir un client en silence
            </h2>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              Le sentiment de ne pas être reconnu
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un client qui fréquente votre commerce depuis 6 mois et que vous traitez encore comme un inconnu reçoit un message implicite : vous n'êtes qu'un numéro. Cette impression ne se traduit jamais en réclamation. Elle se traduit en distance progressive.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'effet inverse est bien documenté. Un prénom utilisé au bon moment, une commande mémorisée sans qu'il ait besoin de la répéter, une attention simple qui prouve que vous l'avez remarqué : ces gestes créent un attachement immédiat. Le <strong className="font-semibold">client reconnu</strong> n'est pas seulement content, il est lié. Retirer la récompense ne le ferait pas partir ; retirer la reconnaissance, si.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              L'absence de contact entre deux achats
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un <strong className="font-semibold">client inactif</strong> ne pense plus à vous. Ce n'est pas de la malveillance : c'est de l'oubli. Si personne ne lui donne de nouvelles entre deux visites, son esprit se remplit d'autre chose. Et quand l'occasion se présente de racheter ce que vous vendez, il pense au concurrent qui, lui, a envoyé un message au bon moment.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un client contacté au moins une fois par mois entre deux visites a un <strong className="font-semibold">taux de rétention</strong> 40 % plus élevé qu'un client sans contact. Le canal importe moins que la régularité. Un message simple, personnalisé, qui arrive au bon moment vaut plus que dix communications génériques.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              La friction accumulée
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Aucun client ne part sur un seul irritant. Il part après une accumulation silencieuse de petits problèmes qui, pris individuellement, ne justifient pas une réclamation : une attente trop longue une fois, une promesse légèrement non tenue, une carte de fidélité difficile à utiliser, une communication trop générique qui ne le concerne pas vraiment.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ces petites frictions s'accumulent sans jamais remonter à la surface. Un jour, quand l'occasion se présente d'essayer un concurrent, le <strong className="font-semibold">client insatisfait silencieux</strong> n'a pas besoin d'une raison supplémentaire. Il avait déjà ses raisons depuis longtemps.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              Une offre concurrente plus personnalisée
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le problème n'est presque jamais le prix. C'est la perception de valeur. Un concurrent qui connaît l'historique d'achat de votre client et lui fait une offre adaptée a plus de poids qu'une remise générique de 10 %. La <strong className="font-semibold">personnalisation</strong> est devenue le standard attendu en 2026, pas un différenciateur. Celui qui ne personnalise pas se retrouve mécaniquement en position de faiblesse face à celui qui le fait.
            </p>

            {/* Section 3 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Les signaux faibles à surveiller avant le départ
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-6">
              Un <strong className="font-semibold">client à risque</strong> ne disparaît pas du jour au lendemain. Il envoie des signaux faibles avant de partir. Ces signaux sont détectables si vous regardez les bons indicateurs.
            </p>

            {/* Tableau signaux faibles */}
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse rounded-xl overflow-hidden text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold">Signal</th>
                    <th className="px-4 py-3 text-left font-semibold">Délai avant départ probable</th>
                    <th className="px-4 py-3 text-left font-semibold">Action immédiate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Fréquence de visite divisée par 2</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">30 à 60 jours</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Message de réactivation personnalisé</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Aucune réponse à 2 communications</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">15 à 30 jours</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Changer de canal et proposer une offre dédiée</td>
                  </tr>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Panier moyen en recul de 30 %</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">60 à 90 jours</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Offre sur sa catégorie habituelle</td>
                  </tr>
                  <tr className="bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Dernier achat supérieur à 45 jours pour un acheteur régulier</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Départ imminent</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Contact direct avec avantage surprise</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              La <strong className="font-semibold">baisse de fréquence de visite</strong> sur les 30 derniers jours est le signal le plus précoce et le plus fiable. Quand un client qui venait toutes les semaines commence à espacer ses visites sans raison apparente, il ne revient pas de lui-même. Il faut aller le chercher.
            </p>

            {/* Image: frequence-visite-client-chute-risque */}
            <img
              src={frequenceImage}
              alt="Courbe de fréquence de visite client montrant une baisse progressive et une zone de risque avant départ"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Section 4 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Comment retenir un client avant qu'il parte : 4 actions préventives
            </h2>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              Le message de réactivation ciblé
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Pas une newsletter générique envoyée à toute votre base : un message qui s'adresse à lui en particulier, basé sur son comportement réel. "Vous n'êtes pas revenu depuis 3 semaines. Voici quelque chose qui pourrait vous intéresser." Cette approche génère un taux de retour 3 à 4 fois supérieur à un message générique.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">notification push wallet</strong> est le canal le plus adapté pour ce type de message : elle arrive directement sur l'écran du téléphone, sans dépendre d'une boîte mail surchargée et sans être soumise à un algorithme.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              La reconnaissance proactive
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Nommer un <strong className="font-semibold">client régulier</strong> et lui signifier qu'il est remarqué est l'action la plus simple et la plus sous-utilisée de la fidélisation. Un message qui dit "Cela fait 6 mois que vous venez régulièrement. Merci." n'a pas de valeur monétaire. Il a une valeur émotionnelle bien plus forte qu'une remise.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              Le rappel de valeur accumulée
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              "Il vous reste 40 points. Encore 2 visites et votre récompense vous attend." Ce message remplit une fonction double : il rappelle l'existence du programme et il crée une raison concrète de revenir dans les prochains jours. Les clients qui ne voient pas leur progression dans un programme l'oublient. Visualiser l'avancement change le comportement.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              L'offre de retour exclusive
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Pour les clients absents depuis 30 à 45 jours, une offre dédiée formulée comme un avantage réservé à eux spécifiquement ("quelque chose rien que pour vous cette semaine") produit des résultats nets. La même remise présentée comme une offre générale a un impact bien inférieur. Le sentiment d'exclusivité déclenche l'action.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">fidélisation émotionnelle</strong> est précisément ce qui retient un client avant qu'il parte en silence. Notre guide sur la fidélisation émotionnelle vs transactionnelle explique pourquoi l'attachement dure quand la récompense seule ne suffit pas.
            </p>

            {/* Section 5 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Client perdu : récupérable ou définitivement parti ?
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Tous les clients perdus ne valent pas le même investissement de récupération. Voici une grille simple pour trier.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un client absent depuis moins de 3 mois est récupérable dans 60 à 70 % des cas avec le bon message au bon moment. La fenêtre est encore ouverte : le lien n'est pas rompu, il est juste distendu.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Entre 3 et 6 mois d'absence, le taux de récupération tombe à 30 à 40 %. Un message simple ne suffit plus : il faut une offre plus forte, un avantage concret qui justifie de revenir.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              Au-delà de 6 mois, le taux de récupération passe sous les 20 %. À ce stade, le coût d'une campagne de reconquête dépasse souvent ce qu'elle rapporte. L'énergie est mieux investie dans la prévention que dans la récupération tardive.
            </p>

            {/* Image: reengagement-client-zones-recuperation */}
            <img
              src={reengagementImage}
              alt="Frise des zones de récupération client avec période optimale de réactivation et baisse des chances dans le temps"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le message qui fonctionne pour réactiver un <strong className="font-semibold">client absent</strong> : reconnaître le temps écoulé sans insister dessus ("ça fait un moment"), proposer une valeur immédiate sans condition, et rendre le retour aussi simple que possible. Un lien direct, un avantage activable en un clic, une démarche fluide.
            </p>

            {/* Conclusion */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4 mt-12">
              Le <strong className="font-semibold">départ silencieux</strong> est évitable dans la majorité des cas. Il survient parce que personne n'a vu les signaux ou n'a pris le temps d'agir. Surveiller 3 indicateurs (fréquence de visite, taux de réponse aux communications, délai depuis le dernier achat) et déclencher une action préventive dès qu'un client décroche : c'est ce qui sépare les structures qui perdent leurs clients de celles qui les gardent. Unotif permet de suivre ces indicateurs et d'envoyer des messages de réactivation ciblés depuis une seule interface.
            </p>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PourquoiClientsPartent;
